import React, { useEffect, useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import dayjs from 'dayjs';
import './TimeNavigationControls.css';

// Import new components
import DateTimePickerIcon from './time-navigation/DateTimePickerIcon';
import TimeDisplay from './time-navigation/TimeDisplay';
import TimeNavigationButtons from './time-navigation/TimeNavigationButtons';
import LeftNavigationButtons from './time-navigation/LeftNavigationButtons';
import RightNavigationButtons from './time-navigation/RightNavigationButtons';

// Import the time navigation hook
import { useTimeNavigation, MIN_TIME } from '../hooks/timeline/useTimeNavigation';

// Time Navigation Controls Component
export interface TimeNavigationProps {
  currentTime: Date | null;
  onTimeChange: (newTime: Date | null) => void;
  showControls: boolean;
  useUTCTime?: boolean;
}

const TimeNavigationControls: React.FC<TimeNavigationProps> = ({
  currentTime,
  onTimeChange,
  showControls,
  useUTCTime = true
}) => {
  const theme = useTheme();
  // Consider both width and orientation for mobile layout
  // Mobile portrait: <600px width
  // Mobile landscape: <900px width in landscape orientation
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(max-width: 899px) and (orientation: landscape)');
  const isMobile = isSmallWidth || isMobileLandscape;
  const [isMounted, setIsMounted] = useState(false);

  // Use the time navigation hook for all time-related logic
  const {
    handleTimeChange,
    handleDateTimePickerChange,
    resetToNow,
    getDatePickerValue,
    getMaxTime,
    currentTime: now
  } = useTimeNavigation(currentTime, onTimeChange, useUTCTime);

  // Prevent hydration issues by only rendering DateTimePicker after mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!showControls) {
    return null;
  }

  if (isMobile) {
    // Mobile: stacked layout
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        mb: 3,
        p: 1,
        backgroundColor: 'rgba(50, 50, 50, 0.5)',
        borderRadius: 1,
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        {/* Navigation buttons */}
        <TimeNavigationButtons
          currentTime={now}
          onTimeChange={handleTimeChange}
          isMobile={isMobile}
          minTime={MIN_TIME}
        />
        
        {/* Date/time display with DateTimePicker icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <TimeDisplay currentTime={now} useUTCTime={useUTCTime} />
          {isMounted && (
            <DateTimePickerIcon
              value={getDatePickerValue()}
              onChange={handleDateTimePickerChange}
              useUTCTime={useUTCTime}
              minDateTime={useUTCTime ? dayjs.utc(MIN_TIME) : dayjs(MIN_TIME)}
              maxDateTime={useUTCTime ? dayjs.utc(getMaxTime()) : dayjs(getMaxTime())}
            />
          )}
        </Box>

        {/* Reset button */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <Button
            onClick={resetToNow}
            variant="text"
            size="small"
            startIcon={<UndoIcon />}
            sx={{
              color: 'primary.main',
              minHeight: '24px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(144, 202, 249, 0.08)'
              }
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>
    );
  }

  // Desktop: responsive layout
  // On wide screens (1536px+): single row with left buttons | time display | right buttons | reset
  // On medium screens: 3 rows (time display, buttons, reset)
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', xl: 'row' },
      alignItems: { xs: 'stretch', xl: 'center' },
      justifyContent: { xs: 'flex-start', xl: 'space-between' },
      gap: { xs: 1.5, xl: 2 },
      mb: 3,
      p: 1.5,
      backgroundColor: 'rgba(50, 50, 50, 0.5)',
      borderRadius: 1,
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      {/* Left navigation buttons - shown on wide screens only */}
      <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center' }}>
        <LeftNavigationButtons
          currentTime={now}
          onTimeChange={handleTimeChange}
          isMobile={isMobile}
          minTime={MIN_TIME}
        />
      </Box>

      {/* Center: Date/time display with picker icon and close/reset button */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        order: { xs: 0, xl: 0 }
      }}>
        <TimeDisplay currentTime={now} useUTCTime={useUTCTime} />
        {isMounted && (
          <DateTimePickerIcon
            value={getDatePickerValue()}
            onChange={handleDateTimePickerChange}
            useUTCTime={useUTCTime}
            minDateTime={useUTCTime ? dayjs.utc(MIN_TIME) : dayjs(MIN_TIME)}
            maxDateTime={useUTCTime ? dayjs.utc(getMaxTime()) : dayjs(getMaxTime())}
          />
        )}
        <Button
          onClick={resetToNow}
          variant="text"
          size="small"
          startIcon={<UndoIcon />}
          sx={{
            color: 'primary.main',
            minHeight: '24px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(144, 202, 249, 0.08)'
            }
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Right navigation buttons - shown on wide screens only */}
      <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center' }}>
        <RightNavigationButtons
          currentTime={now}
          onTimeChange={handleTimeChange}
          isMobile={isMobile}
        />
      </Box>

      {/* Medium screens: Navigation buttons row (shown below time display on screens < 1536px) */}
      <Box sx={{
        display: { xs: 'flex', xl: 'none' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        order: { xs: 1, xl: 1 }
      }}>
        <LeftNavigationButtons
          currentTime={now}
          onTimeChange={handleTimeChange}
          isMobile={isMobile}
          minTime={MIN_TIME}
        />
        <RightNavigationButtons
          currentTime={now}
          onTimeChange={handleTimeChange}
          isMobile={isMobile}
        />
      </Box>
    </Box>
  );
};

export default TimeNavigationControls;